import "@shoelace-style/shoelace/dist/themes/light.css";
import { SlSelect, SlOption } from "@shoelace-style/shoelace/dist/react";
import { FC, useCallback, useEffect, useState, useRef } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import DatePicker from "react-datepicker";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  accountValidationSchema,
  IndustryOption,
  TagOption,
} from "./schema/accountSchema";
import { useSearchParams } from "react-router-dom";
import Error from "src/components/Error";
import AccountReminderList from "./AccountReminderList";
import ToolTip from "src/components/ToolTip";
import styles from "./AccountsIsland.module.css";
import ProgressBar from "src/components/ProgressBar";
import Warning from "src/components/Warning";
import FileUploadContainer from "src/components/FileUploadContainer";
import { useDropzone } from "react-dropzone";
import axios, { AxiosProgressEvent } from "axios";
import {
  formatCardDateTime,
  toLocalDateString,
  parseDateAsLocal,
} from "src/utils/formatUtils";
import { Grid } from "react-loader-spinner";
import AccountTags from "./AccountTags";

interface ApiResponse {
  type: "success" | "error" | "info" | "warning";
  message: string;
}

type AccountDocumentDTO = {
  accountDocumentId: number;
  blobFileUrl: string;
  fileName: string;
  uploadedDate: Date;
  uploadedBy: string;
  description: string;
};

type AccountDTO = {
  acctId?: string;
  name?: string;
  dialerType?: string[];
  noAutoRenew?: boolean;
  contractExpiration?: Date | null;
  termCommitment?: boolean;
  reminderNote?: string;
  reminderDate?: Date | null;
  reminderType?: "One Time" | "Recurring";
  reminderFrequency?: "Monthly" | "Quarterly" | "Semi Annual" | "Annual";
  tags?: string[];
  industryId?: number | null;
  industryOptions?: IndustryOption[];
  tagOptions?: TagOption[];
};

const AccountsIsland: FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showReminderModal, setShowReminderModal] = useState<boolean>(false);
  const [showTagsModal, setShowTagsModal] = useState<boolean>(false);
  const [accountDocuments, setAccountDocuments] = useState<
    AccountDocumentDTO[]
  >([]);

  const [searchParams] = useSearchParams();
  const acctId = searchParams.get("acctId");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [docToDelete, setDocToDelete] = useState<number | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [accountDescription, setAccountDescription] = useState<string>("");
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null);
  const [showEditDescModal, setShowEditDescModal] = useState(false);

  const [isAllowedToCreateFreeTag, setIsAllowedToCreateFreeTag] =
    useState(false);

  // Reminder state
  const [reminderType, setReminderType] = useState<"One Time" | "Recurring">(
    "One Time",
  );

  const industrySelectRef = useRef<any>(null);
  const tagsSelectRef = useRef<any>(null);
  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AccountDTO>({
    resolver: zodResolver(accountValidationSchema),
  });

  // Watch the form values
  const watchedIndustryId = watch("industryId");
  const watchedTags = watch("tags");

  // Load Bootstrap icons CSS for this island component
  useEffect(() => {
    const linkId = "bootstrap-icons-accounts-island";

    // Check if the link already exists
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.href =
        "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.5/font/bootstrap-icons.css";
      document.head.appendChild(link);
    }

    // Cleanup function to remove the CSS when component unmounts
    return () => {
      const existingLink = document.getElementById(linkId);
      if (existingLink) {
        document.head.removeChild(existingLink);
      }
    };
  }, []);

  const handleApiResponse = useCallback((response: ApiResponse) => {
    switch (response.type) {
      case "success":
        setSuccessMessage(response.message);
        setErrorMessage("");
        break;
      case "error":
        setErrorMessage(response.message);
        setSuccessMessage("");
        break;
    }
  }, []);

  const onSubmit = useCallback(
    async (data: any) => {
      setIsLoading(true);
      setSuccessMessage("");
      setErrorMessage("");

      const accountToUpdate = {
        AcctId: acctId,
        ...data,
        reminderDate: toLocalDateString(data.reminderDate),
        contractExpiration: toLocalDateString(data.contractExpiration),
        reminderType: reminderType,
      };

      try {
        var response = await callApi(
          "sysadmin/accounts/UpdateAccount",
          HttpMethod.POST,
          accountToUpdate,
        );
        if (response) {
          setSuccessMessage("Successfully saved Account");
        }
      } catch (error) {
        console.error("Failed to save account", error);
        setErrorMessage("Failed to save account. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [acctId, reminderType],
  );
  const getAccountDocuments = useCallback(async () => {
    if (!acctId) return;
    setIsLoading(true);
    try {
      let apiUrl = `sysadmin/accounts/GetAccountDocuments?acctId=${encodeURIComponent(acctId)}`;
      const response = await callApi(apiUrl, HttpMethod.GET);
      if (response) {
        setAccountDocuments(response);
      }
    } catch (error) {
      console.error("Failed to load account documents", error);
      setErrorMessage("Failed to load account documents. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [acctId]);

  const getAccount = useCallback(async () => {
    setIsLoading(true);
    setSuccessMessage("");
    setErrorMessage("");
    try {
      let apiUrl = `sysadmin/accounts/GetAccount?acctId=${encodeURIComponent(acctId!)}`;
      var response = await callApi(apiUrl, HttpMethod.GET);
      if (response) {
        const processedData = {
          dialerType: response.dialerType ?? [],
          noAutoRenew: response.noAutoRenew ?? false,
          contractExpiration: parseDateAsLocal(response.contractExpiration),
          termCommitment: response.termCommitment ?? false,
          reminderNote: response.reminderNote ?? "",
          reminderDate: parseDateAsLocal(response.reminderDate),
          reminderType: response.reminderType ?? "One Time",
          reminderFrequency: response.reminderFrequency ?? "Monthly",
          tags: response.tags
            ? response.tags.map((tag: any) => tag.toString())
            : [],
          industryId: response.industryId ? Number(response.industryId) : null,
          industryOptions: response.industries ?? [],
          tagOptions: response.tagOptions ?? [],
        };
        reset(processedData);

        setIsAllowedToCreateFreeTag(response.isUserAllowCreateFreeTag);
      }
    } catch (error) {
      console.error("Failed to load account", error);
      setErrorMessage("Failed to load account data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [acctId, reset]);

  useEffect(() => {
    if (!acctId) {
      setErrorMessage("Missing acctId in query string.");
      return;
    }
    getAccount();
  }, [acctId, getAccount]);

  // Set Shoelace component values when options and values are loaded
  useEffect(() => {
    if (
      watchedIndustryId &&
      control._formValues.industryOptions &&
      control._formValues.industryOptions.length > 0
    ) {
      setTimeout(async () => {
        if (industrySelectRef.current) {
          try {
            // Wait for the component to be ready
            await industrySelectRef.current.updateComplete;
            industrySelectRef.current.value = watchedIndustryId.toString();
          } catch (error) {
            console.error("Error setting industry value:", error);
          }
        }
      }, 100);
    }
  }, [watchedIndustryId, control._formValues.industryOptions]);

  useEffect(() => {
    if (
      watchedTags &&
      watchedTags.length > 0 &&
      control._formValues.tagOptions &&
      control._formValues.tagOptions.length > 0
    ) {
      setTimeout(async () => {
        if (tagsSelectRef.current) {
          try {
            // Wait for the component to be ready
            await tagsSelectRef.current.updateComplete;
            tagsSelectRef.current.value = watchedTags;
          } catch (error) {
            console.error("Error setting tags value:", error);
          }
        }
      }, 100);
    }
  }, [watchedTags, control._formValues.tagOptions]);
  useEffect(() => {
    getAccountDocuments();
  }, [getAccountDocuments]);

  // Sync form values with local state for reminder type
  useEffect(() => {
    const formValues = control._formValues;
    if (formValues.reminderType) {
      setReminderType(formValues.reminderType);
    }
  }, [control._formValues.reminderType]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setIsLoading(true);
      setUploadProgress(0);
      setErrorMessage("");
      setSuccessMessage("");

      const file = acceptedFiles[0];
      const fileNameWithExtension = file.name;

      if (!acctId) {
        setErrorMessage(
          "Cannot read acctId. acctId is required for document upload",
        );
        setIsLoading(false);
        return;
      }

      try {
        // Step 1: Get Upload URL from API
        const initiateResponse: ApiResponse & { uploadUrl?: string } =
          await callApi(`sysadmin/accounts/upload_initiate?acctId=${encodeURIComponent(acctId)}`, HttpMethod.POST, {
            fileName: fileNameWithExtension,
          });

        if (initiateResponse.type === "error" || !initiateResponse.uploadUrl) {
          handleApiResponse(initiateResponse);
          setIsLoading(false);
          return;
        }

        const blobUploadUrl = initiateResponse.uploadUrl;

        // Step 2: Upload the file to Azure Blob
        await axios({
          method: "put",
          url: blobUploadUrl,
          data: file,
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type,
          },
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            const total = progressEvent.total || progressEvent.loaded;
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / total,
            );
            setUploadProgress(percentCompleted);
          },
        });

        // Step 3: Notify backend of the uploaded file
        const fileURL = blobUploadUrl.split("?")[0]; // remove SAS token
        const submitPayload = {
          acctId,
          blobFileUrl: fileURL,
          fileName: fileNameWithExtension,
          fileSize: file.size,
          contentType: file.type,
          description: accountDescription,
        };

        const submitResponse: ApiResponse = await callApi(
          "sysadmin/accounts/upload_contract",
          HttpMethod.POST,
          submitPayload,
        );
        handleApiResponse(submitResponse);
      } catch (error: any) {
        const response = error?.response?.data;
        const fallback: ApiResponse = {
          type: "error",
          message:
            response?.message ||
            "There was an error uploading your document. Please try again or contact support.",
        };

        handleApiResponse(response || fallback);
      } finally {
        setIsLoading(false);
        setUploadProgress(0);
        getAccountDocuments();
        setAccountDescription("");
      }
    },
    [acctId, accountDescription, handleApiResponse, getAccountDocuments],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  });

  const downloadAccountDocumentById = useCallback(
    async (docId: number) => {
      if (!docId || !acctId) return;
      setIsLoading(true);

      try {
        const apiUrl = `sysadmin/accounts/DownloadAccountDocument?docId=${docId}&acctId=${encodeURIComponent(acctId)}`;
        const response = await callApi(apiUrl, HttpMethod.GET);
        const { base64, fileName, contentType } = response;
        // Decode base64 and convert to blob
        const byteCharacters = atob(base64);
        const byteArrays = [];

        for (let i = 0; i < byteCharacters.length; i += 512) {
          const slice = byteCharacters.slice(i, i + 512);
          const byteNumbers = new Array(slice.length);
          for (let j = 0; j < slice.length; j++) {
            byteNumbers[j] = slice.charCodeAt(j);
          }
          const byteArray = new Uint8Array(byteNumbers);
          byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName || "downloaded-file";
        link.click();
        window.URL.revokeObjectURL(url);
        handleApiResponse({
          type: "success",
          message: "Document downloaded successfully.",
        });
      } catch (error: any) {
        console.error("Failed to download account document:", error);
        const errorResponse: ApiResponse = error?.response?.data ?? {
          type: "error",
          message: "Failed to download account document. Please try again.",
        };
        handleApiResponse(errorResponse);
      } finally {
        setIsLoading(false);
      }
    },
    [acctId, handleApiResponse],
  );

  const handleDeleteClick = (docId: number) => {
    setDocToDelete(docId);
    setShowConfirmModal(true);
  };
  const deleteAccountDocumentById = useCallback(async () => {
    if (!docToDelete) return;
    setIsLoading(true);
    try {
      const apiUrl = `sysadmin/accounts/DeleteAccountDocument/${docToDelete}`;
      const response: ApiResponse = await callApi(apiUrl, HttpMethod.DELETE);

      setAccountDocuments(
        (prevDocs) =>
          prevDocs?.filter((doc) => doc.accountDocumentId !== docToDelete) ||
          [],
      );

      handleApiResponse(response);
    } catch (error: any) {
      console.error("Failed to delete account document:", error);

      const errorResponse: ApiResponse = error?.response?.data ?? {
        type: "error",
        message: "Failed to delete account document. Please try again.",
      };

      handleApiResponse(errorResponse);
    } finally {
      setShowConfirmModal(false);
      setDocToDelete(null);
      setIsLoading(false);
    }
  }, [docToDelete, handleApiResponse]);

  const handleSaveDescription = (docId: number, description: string) => {
    setSelectedDocId(docId);
    setShowEditDescModal(true);
    setAccountDescription(description);
  };

  const updateAccountDescription = useCallback(async () => {
    if (!selectedDocId) return;
    setIsLoading(true);
    setShowEditDescModal(false);
    try {
      const apiUrl = `sysadmin/accounts/UpdateAccountDocumentDesc`;
      const submitPayload = {
        id: selectedDocId,
        description: accountDescription,
      };

      const response: ApiResponse = await callApi(
        apiUrl,
        HttpMethod.POST,
        submitPayload,
      );

      handleApiResponse(response);
    } catch (error: any) {
      console.error("Failed to update account document description:", error);

      const errorResponse: ApiResponse = error?.response?.data ?? {
        type: "error",
        message:
          "Failed to update account document description. Please try again.",
      };

      handleApiResponse(errorResponse);
    } finally {
      setSelectedDocId(null);
      setAccountDescription("");
      setIsLoading(false);
      getAccountDocuments();
    }
  }, [
    selectedDocId,
    accountDescription,
    handleApiResponse,
    getAccountDocuments,
  ]);

  return (
    <>
      {isLoading && (
        <div className={styles.loaderAccountContainer}>
          <Grid
            height="40"
            width="40"
            color="#0eb0e5"
            ariaLabel="grid-loading"
            radius="12.5"
            wrapperStyle={{}}
            wrapperClass=""
            visible={isLoading}
          />
        </div>
      )}
      <div
        className="p-1 m-0 border-0 m-0 border-0"
        style={{
          maxHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "10px",
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          {isLoading && <div className="loader-container"></div>}

          {successMessage && (
            <div
              className="alert alert-success alert-dismissible fade show"
              role="alert"
            >
              {successMessage}
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setSuccessMessage("")}
              ></button>
            </div>
          )}

          {errorMessage ? (
            <Error
              message={`${errorMessage} Cannot show account configuration.`}
            />
          ) : (
            <>
              <div className={`row ${styles.equalHeightRow}`}>
                <div className={`col-md-6 ${styles.equalHeightCol}`}>
                  <div className={`card ${styles.equalHeightCard}`}>
                    <div className="card-header">Account Information</div>
                    <div className={`card-body ${styles.equalHeightCardBody}`}>
                      <div className="row">
                        <div className="col-md-12">
                          <div className="row">
                            <label className="col-md-3">Dialer </label>
                            <div className="col-md-9">
                              <Controller
                                name="dialerType"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <SlSelect
                                    multiple
                                    clearable
                                    placement="bottom"
                                    placeholder="  Click to select customer dialer."
                                    onSlChange={onChange}
                                    value={value ?? []}
                                  >
                                    <SlOption value="dialpad">Dialpad</SlOption>
                                    <SlOption value="five9">Five9</SlOption>
                                    <SlOption value="genesys">Genesys</SlOption>
                                    <SlOption value="ringcentral">
                                      RingCentral
                                    </SlOption>
                                    <SlOption value="talkdesk">
                                      Talkdesk
                                    </SlOption>
                                    <SlOption value="twilio-flex">
                                      Twilio Flex
                                    </SlOption>
                                    <SlOption value="vonage">Vonage</SlOption>
                                    <SlOption value="zoom-phone">
                                      Zoom Phone
                                    </SlOption>
                                    <SlOption value="3cx">3CX</SlOption>
                                    <SlOption value="avaya">Avaya</SlOption>
                                    <SlOption value="cisco-webex">
                                      Cisco Webex Calling
                                    </SlOption>
                                    <SlOption value="cloudtalk">
                                      CloudTalk
                                    </SlOption>
                                    <SlOption value="dixa">Dixa</SlOption>
                                    <SlOption value="freshcaller">
                                      Freshcaller
                                    </SlOption>
                                    <SlOption value="hubspot">
                                      HubSpot Calling
                                    </SlOption>
                                    <SlOption value="jive">
                                      Jive Communications
                                    </SlOption>
                                    <SlOption value="justcall">
                                      JustCall
                                    </SlOption>
                                    <SlOption value="microsoft-teams">
                                      Microsoft Teams
                                    </SlOption>
                                    <SlOption value="nextiva">Nextiva</SlOption>
                                    <SlOption value="nice-incontact">
                                      NICE inContact
                                    </SlOption>
                                    <SlOption value="other">Other</SlOption>
                                    {/* Add more options as needed */}
                                  </SlSelect>
                                )}
                              />
                            </div>
                          </div>
                          <div className="row mb-2 mt-2">
                            <div className="col-md-3"></div>
                            <div className="col-md-9">
                              <div className="row">
                                <div className="col-md-6">
                                  <Controller
                                    name="noAutoRenew"
                                    control={control}
                                    defaultValue={false}
                                    render={({ field }) => (
                                      <div className="form-check">
                                        <input
                                          className="me-1 form-check-input"
                                          type="checkbox"
                                          id="noAutoRenew"
                                          checked={field.value ?? false}
                                          onChange={(e) =>
                                            field.onChange(e.target.checked)
                                          }
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor="noAutoRenew"
                                        >
                                          No Auto Renew
                                          <ToolTip
                                            description="The contract does not automatically renew for another term"
                                            placement="bottom"
                                          />
                                        </label>
                                      </div>
                                    )}
                                  />
                                </div>
                                <div className="col-md-6">
                                  <Controller
                                    name="termCommitment"
                                    control={control}
                                    defaultValue={false}
                                    render={({ field }) => (
                                      <div className="form-check">
                                        <input
                                          className="me-1 form-check-input"
                                          type="checkbox"
                                          id="termCommitment"
                                          checked={field.value ?? false}
                                          onChange={(e) =>
                                            field.onChange(e.target.checked)
                                          }
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor="termCommitment"
                                        >
                                          Term Commitment
                                          <ToolTip
                                            description="The customer agrees not to cancel the account before the contract's end date"
                                            placement="bottom"
                                          />
                                        </label>
                                      </div>
                                    )}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <label className="col-md-3">Industry </label>
                        <div className="col-md-9">
                          <Controller
                            name="industryId"
                            control={control}
                            render={({ field: { onChange, value } }) => {
                              return (
                                <SlSelect
                                  ref={industrySelectRef}
                                  placement="bottom"
                                  placeholder="Click to select industry"
                                  onSlChange={(e) => {
                                    const selectedValue = (e.target as any)
                                      ?.value;
                                    const parsedValue = selectedValue
                                      ? parseInt(selectedValue)
                                      : null;
                                    onChange(parsedValue);
                                  }}
                                  onSlShow={() => {
                                    if (value && industrySelectRef.current) {
                                      industrySelectRef.current.value =
                                        value.toString();
                                    }
                                  }}
                                  onSlAfterShow={() => {
                                    if (value && industrySelectRef.current) {
                                      industrySelectRef.current.value =
                                        value.toString();
                                    }
                                  }}
                                  value={value ? value.toString() : ""}
                                >
                                  {control._formValues.industryOptions?.map(
                                    (opt: IndustryOption) => (
                                      <SlOption
                                        key={opt.id}
                                        value={opt.id.toString()}
                                      >
                                        {opt.industryName}
                                      </SlOption>
                                    ),
                                  )}
                                </SlSelect>
                              );
                            }}
                          />
                        </div>
                      </div>
                      <div className="row mb-3">
                        <label className="col-md-3">
                          Contract Expiration
                          <ToolTip
                            description="If contract expires on an explicit date, specify expiration date."
                            placement="bottom"
                          />
                        </label>
                        <div className="col-md-9">
                          <Controller
                            name="contractExpiration"
                            control={control}
                            render={({
                              field: { onChange, onBlur, value, name },
                            }) => (
                              <>
                                <DatePicker
                                  name={name}
                                  className={"form-control"}
                                  onChange={onChange}
                                  onBlur={onBlur}
                                  selected={value}
                                  placeholderText={"MM/DD/YYYY"}
                                  dateFormat="MM/dd/yyyy"
                                  isClearable
                                />
                              </>
                            )}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <label className="col-md-3">Tags</label>
                        <div className="col-md-9">
                          <Controller
                            name="tags"
                            control={control}
                            defaultValue={[]}
                            render={({ field: { onChange, value } }) => {
                              return (
                                <div className="row">
                                  <div
                                    className={
                                      isAllowedToCreateFreeTag
                                        ? "col-md-10"
                                        : "col-md-12"
                                    }
                                  >
                                    <SlSelect
                                      ref={tagsSelectRef}
                                      multiple
                                      clearable
                                      value={value || []}
                                      placement="bottom"
                                      placeholder="  Click to select tag(s)"
                                      onSlChange={onChange}
                                      onSlShow={() => {
                                        if (
                                          value &&
                                          value.length > 0 &&
                                          tagsSelectRef.current
                                        ) {
                                          tagsSelectRef.current.value = value;
                                        }
                                      }}
                                      onSlAfterShow={() => {
                                        if (
                                          value &&
                                          value.length > 0 &&
                                          tagsSelectRef.current
                                        ) {
                                          tagsSelectRef.current.value = value;
                                        }
                                      }}
                                    >
                                      {control._formValues.tagOptions?.map(
                                        (opt: TagOption) => (
                                          <SlOption
                                            key={opt.id}
                                            value={opt.id.toString()}
                                          >
                                            {opt.tag}
                                          </SlOption>
                                        ),
                                      )}
                                    </SlSelect>
                                  </div>
                                  {isAllowedToCreateFreeTag && (
                                    <div className="col-md-2">
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        onClick={() => setShowTagsModal(true)}
                                      >
                                        <i className="bi bi-pencil-square"></i>{" "}
                                        Tags
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`col-md-6 ${styles.equalHeightCol}`}>
                  <div className={`card ${styles.equalHeightCard}`}>
                    <div className="card-header">Reminder Settings</div>
                    <div className={`card-body ${styles.equalHeightCardBody}`}>
                      <div className="row">
                        <div className="col-md-12">
                          <div className="mb-3 row">
                            <label className="col-md-4">Reminder Type</label>
                            <div className="col-md-8">
                              <Controller
                                name="reminderType"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <select
                                    className="form-select"
                                    value={value || "One Time"}
                                    onChange={(e) => {
                                      onChange(e.target.value);
                                      setReminderType(
                                        e.target.value as
                                          | "One Time"
                                          | "Recurring",
                                      );
                                    }}
                                  >
                                    <option value="One Time">One Time</option>
                                    <option value="Recurring">Recurring</option>
                                  </select>
                                )}
                              />
                            </div>
                          </div>

                          {reminderType === "Recurring" && (
                            <div className="mb-3 row">
                              <label className="col-md-4">Frequency</label>
                              <div className="col-md-8">
                                <Controller
                                  name="reminderFrequency"
                                  control={control}
                                  render={({ field: { onChange, value } }) => (
                                    <select
                                      className="form-select"
                                      value={value || "Monthly"}
                                      onChange={(e) => {
                                        onChange(e.target.value);
                                      }}
                                    >
                                      <option value="Monthly">Monthly</option>
                                      <option value="Quarterly">
                                        Quarterly
                                      </option>
                                      <option value="Semi Annual">
                                        Semi Annual
                                      </option>
                                      <option value="Annual">Annual</option>
                                    </select>
                                  )}
                                />
                              </div>
                            </div>
                          )}

                          <div className="mb-3 row">
                            <label className="col-md-4">
                              {reminderType === "Recurring"
                                ? "Start Date"
                                : "Reminder Date"}
                            </label>
                            <div className="col-md-8">
                              <Controller
                                name="reminderDate"
                                control={control}
                                render={({
                                  field: { onChange, onBlur, value, name },
                                }) => (
                                  <DatePicker
                                    name={name}
                                    className={"form-control"}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    selected={value}
                                    placeholderText={"MM/DD/YYYY"}
                                    dateFormat="MM/dd/yyyy"
                                    isClearable
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-12">
                          <div className="mb-3 row">
                            <label className="col-md-4 col-form-label">
                              Scheduler Note
                            </label>
                            <div className="col-md-8">
                              <Controller
                                name="reminderNote"
                                control={control}
                                defaultValue={""}
                                render={({
                                  field: { onChange, onBlur, value, name },
                                }) => (
                                  <textarea
                                    name={name}
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    onBlur={onBlur}
                                    className={`form-control ${errors.reminderNote ? "is-invalid" : ""}`}
                                    rows={4}
                                    placeholder="Enter reminder note"
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-12 mt-3">
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => setShowReminderModal(true)}
                          >
                            <i className="bi bi-calendar-check"></i> View
                            Reminder List for {acctId}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="mt-3 btn btn w-100 btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="bi bi-save"></i> Save
                  </>
                )}
              </button>
            </>
          )}
        </form>
        <div className="card mt-3">
          <div className="card-header">Account Documents</div>
          <div className="card-body">
            <div className="row mt-3">
              <div className="col-md-6">
                <div className="row">
                  <div className="col-md-12">
                    <div className="d-flex flex-wrap">
                      <FileUploadContainer
                        getRootProps={getRootProps}
                        getInputProps={getInputProps}
                        isDragActive={isDragActive}
                        title="UPLOAD CONTRACT FILE"
                        iconClassName="bi-box-arrow-up"
                      />
                    </div>
                    {uploadProgress > 0 && (
                      <ProgressBar progress={uploadProgress} />
                    )}
                    {errorMessage && <Warning message={errorMessage} />}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="row">
                  <div className="col-md-12">
                    <textarea
                      className="form-control"
                      rows={4}
                      value={accountDescription}
                      onChange={(e) => setAccountDescription(e.target.value)}
                      placeholder="Add a description for the document"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 mt-3">
                {accountDocuments.length > 0 ? (
                  <table className="table table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>
                          <i className="bi bi-file-earmark"></i> File Name
                        </th>
                        <th>Description</th>
                        <th>Uploaded By</th>
                        <th>Uploaded Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accountDocuments.map((doc) => {
                        return (
                          <tr key={doc.accountDocumentId}>
                            <td>
                              {" "}
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault(); // Prevent the default anchor behavior
                                  downloadAccountDocumentById(
                                    doc.accountDocumentId,
                                  );
                                }}
                                style={{
                                  color: "#007bff",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                }}
                              >
                                {doc.fileName}
                              </a>
                            </td>
                            <td>{doc.description}</td>
                            <td>{doc.uploadedBy}</td>
                            <td>
                              {formatCardDateTime(doc.uploadedDate.toString())}
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() =>
                                    handleDeleteClick(doc.accountDocumentId)
                                  }
                                >
                                  <i className="bi bi-trash"></i> Delete
                                </button>

                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() =>
                                    handleSaveDescription(
                                      doc.accountDocumentId,
                                      doc.description,
                                    )
                                  }
                                >
                                  <i className="bi bi-pencil-fill"></i> Edit
                                  Description
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-muted">No account documents found.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tags Modal */}
        {showTagsModal && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex={-1}
            role="dialog"
            aria-labelledby="tagsModalLabel"
            aria-hidden="false"
          >
            <div className="modal-dialog modal-xl" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="tagsModalLabel">
                    Manage Tags
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowTagsModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <AccountTags />
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowTagsModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reminder List Modal */}
        {showReminderModal && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex={-1}
            role="dialog"
            aria-labelledby="reminderModalLabel"
            aria-hidden="false"
          >
            <div
              className="modal-dialog modal-xl"
              role="document"
              style={{ maxWidth: "90%" }}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="reminderModalLabel">
                    Reminder List for Account {acctId}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowReminderModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <AccountReminderList acctId={acctId || ""} isModal={true} />
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowReminderModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showConfirmModal && (
          <div className="modal fade show d-block " role="dialog">
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Deletion</h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    onClick={() => setShowConfirmModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to delete this account document?</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowConfirmModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={deleteAccountDocumentById}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showEditDescModal && (
          <div className="modal fade show d-block " role="dialog">
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Description</h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    onClick={() => setShowEditDescModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>Account Description</p>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={accountDescription}
                    onChange={(e) => setAccountDescription(e.target.value)}
                    placeholder="Update a description for the document"
                  ></textarea>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowEditDescModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={updateAccountDescription}
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AccountsIsland;
